import {FC, useContext} from "react";
import { ServicingAccessPoint } from "@app/projects/network-assessment/provider-hub/interfaces";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import ProviderActionStatusBadge from "@app/shared/ProviderActionStatusBadge";
import {ProviderHubContext} from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { ProviderBadgeStyle } from "@app/projects/enumerators";
import { locationConfidenceScoreToDisplay } from "@app/projects/utility";
import { NetworkIntegrityNotificationFlag } from "@app/projects/network-assessment/provider-hub/network-integrity-components/NetworkIntegrityNotificationFlag";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { SpecialtyPredictionModelEnum } from "@app/projects/servicing-provider/provider-claims/SpecialtyPredictionModelEnum";
import { formatSpecialtyWithCode } from "@app/projects/network-assessment/provider-hub/utility";

interface AccessPointCardProps {
  accessPoint: ServicingAccessPoint;
  index: number;
  isSelected: boolean;
  onSelectAccessPoint: (newAccessPoint: ServicingAccessPoint) => void;
}

export const AccessPointCard: FC<AccessPointCardProps> = (props: AccessPointCardProps) => {
  const { accessPoint } = props;
  const providerHubContext = useContext(ProviderHubContext);
  const featureFlagContext = useContext(FeatureFlagContext);

  let spmClass = "";

  switch (accessPoint.specialtyPredictionModel) {
    case SpecialtyPredictionModelEnum.Match:
      spmClass = "match";
      break;
    case SpecialtyPredictionModelEnum.Mismatch:
      spmClass = "mismatch";
      break;
  }

  return (
    <div
      className={`provider-profile__access-point-card${props.isSelected ? "--selected" : ""}`}
      onClick={() => props.onSelectAccessPoint(accessPoint)}
      data-testid={`provider-profile__access-point-card--${props.index}`}
    >
      <h6>
        {accessPoint.address}<br />
        {!!accessPoint.address2 ? <>{accessPoint.address2}<br /></> : null}
        {accessPoint.city}, {accessPoint.state} {accessPoint.zip}
        {featureFlagContext.enabledFeatureFlags.has(Features.ENABLE_NETWORK_INTEGRITY_IN_PROVIDER_HUB) && (
          <NetworkIntegrityNotificationFlag hasNetworkIntegrityFailure={accessPoint.hasNetworkIntegrityFailure} />
        )}
      </h6>
      <dl className="provider-profile__access-point-info">
        <RoleRestricted allowedRoles={[Roles.TIN_VIEWER]}>
          {accessPoint.tin &&
            <div className="provider-profile__access-point-row">
              <dt>TIN:</dt>
              <dd>{accessPoint.tin}</dd>
            </div>
          }
        </RoleRestricted>

        <div className="provider-profile__access-point-row">
          <dt>County:</dt>
          <dd>
            {accessPoint.countyName ? <>{accessPoint.countyName}, {accessPoint.state}</> : "---"}
          </dd>
        </div>

        <div className="provider-profile__access-point-row">
          <dt>Specialty Group:</dt>
          <dd>{accessPoint.specialtyGroupName ? formatSpecialtyWithCode({ code: accessPoint.specialtyGroupCode, name: accessPoint.specialtyGroupName }) : "---"}</dd>
        </div>

        <div className="provider-profile__access-point-row">
          <dt>Specialty:</dt>
          <dd>{accessPoint.specialtyDescription ? formatSpecialtyWithCode({ code: accessPoint.specialtyCode, name: accessPoint.specialtyDescription }) : "---"}</dd>
        </div>

        <div className="provider-profile__access-point-row">
          <dt>Provider Source:</dt>
          <dd>{accessPoint.providerNetworkType ? <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} providerNetworkType={accessPoint.providerNetworkType} /> : "---"}</dd>
        </div>

        {!providerHubContext.isScenarioModelingProcessing && featureFlagContext.enabledFeatureFlags.has(Features.PROVIDER_HUB_PROJECTION) &&
          <div className="provider-profile__access-point-row">
            <dt>Provider Action Status:</dt>
            <dd>
              <ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot} status={accessPoint.providerActionStatus}/>
            </dd>
          </div>
        }

        {providerHubContext.showAccessPointLocationConfidence &&
          <div className="provider-profile__access-point-row">
            <dt>Location Confidence:</dt>
            <dd>{locationConfidenceScoreToDisplay(accessPoint.locationConfidenceScore)}</dd>
          </div>
        }

        {featureFlagContext.enabledFeatureFlags.has(Features.NETWORK_PERFORMANCE_SPECIALTY_PREDICTION_MODEL) &&
          <div className="provider-profile__access-point-row--specialty-prediction">
            <dt>Specialty Prediction:</dt>
            <dd>
              <span className={spmClass}>{accessPoint.specialtyPredictionModel ?? '---'}</span>
            </dd>
          </div>
        }
      </dl>
    </div>
  );
}